import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import classNames from 'classnames';
import {
  Receipt,
  BarChart2,
  Activity,
  Clock,
  Siren,
  ShieldCheck,
  BellRing,
  Send,
  Sparkles,
  ClipboardCheck,
  X,
  type LucideIcon,
} from 'lucide-react';
import { authService } from '../../auth/authService';
import { canAccessPage } from '../../utils/permissions';
import { ANALYTICS_CHILDREN, isAnalyticsChildActive } from '../../utils/analyticsConfig';
import { useOptionalResponsiveLayout } from '../../context/ResponsiveLayoutContext';
import './AnalyticsSubSidebar.css';

const CHILD_ICONS: Record<string, LucideIcon> = {
  Receipt,
  BarChart2,
  Activity,
  Clock,
  Siren,
  ShieldCheck,
  BellRing,
  Send,
  Sparkles,
  ClipboardCheck,
};

function AnalyticsSubSidebar() {
  const navigate = useNavigate();
  const location = useLocation();
  const user = authService.getUser();
  const layout = useOptionalResponsiveLayout();
  const isDrawerMode = layout?.isDrawerMode ?? false;
  const isOpen = layout ? layout.isAnalyticsNavOpen : true;

  const visibleChildren = ANALYTICS_CHILDREN.filter(child => canAccessPage(user, child.path));

  const handleNavigate = (path: string) => {
    navigate(path);
    if (isDrawerMode) {
      layout?.closeAllNav();
    }
  };

  return (
    <aside
      className={classNames(
        'analytics-sub-sidebar',
        isDrawerMode && 'analytics-sub-sidebar--drawer',
        isDrawerMode && isOpen && 'analytics-sub-sidebar--open'
      )}
      aria-label="Analytics navigation"
      aria-hidden={isDrawerMode && !isOpen ? true : undefined}
    >
      <div className="analytics-sub-sidebar-header-row">
        <div className="analytics-sub-sidebar-header">Analytics</div>
        {isDrawerMode ? (
          <button
            type="button"
            className="analytics-sub-sidebar-close"
            onClick={layout?.closeAllNav}
            aria-label="Close analytics navigation"
          >
            <X
              size={18}
              strokeWidth={2}
            />
          </button>
        ) : null}
      </div>
      <nav className="analytics-sub-sidebar-nav">
        {visibleChildren.map(child => {
          const active = isAnalyticsChildActive(location.pathname, child);
          const Icon = CHILD_ICONS[child.icon];
          return (
            <button
              key={child.id}
              type="button"
              className={`analytics-sub-sidebar-item ${active ? 'active' : ''}`}
              onClick={() => handleNavigate(child.path)}
              aria-current={active ? 'page' : undefined}
            >
              {Icon ? (
                <Icon
                  className="analytics-sub-sidebar-item-icon"
                  size={16}
                  strokeWidth={2}
                />
              ) : null}
              <span className="analytics-sub-sidebar-item-label">{child.label}</span>
            </button>
          );
        })}
      </nav>
    </aside>
  );
}

export default AnalyticsSubSidebar;
